import React from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  ShieldCheckIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ServerIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  InformationCircleIcon,
  GlobeAmericasIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CloudIcon,
  ComputerDesktopIcon,
  FolderIcon,
  ClockIcon,
  ShareIcon,
  UserIcon,
  BellIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const PrivacyWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f3f4f6 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.03) 0%, transparent 70%);
    animation: ${gradientShift} 20s ease infinite;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  padding: var(--spacing-4xl) 0;
  position: relative;
  overflow: hidden;
  margin-top: -80px;
  padding-top: calc(var(--spacing-4xl) + 80px);
  
  @media (min-width: 768px) {
    margin-top: -90px;
    padding-top: calc(var(--spacing-4xl) + 90px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  position: relative;
  z-index: 1;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  margin-bottom: var(--spacing-xl);
  animation: ${fadeIn} 0.6s ease-out;
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  max-width: 600px;
  margin: 0 auto var(--spacing-xl) auto;
  line-height: 1.8;
  animation: ${fadeIn} 1s ease-out;
`;

const LastUpdatedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.15);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  animation: ${fadeIn} 1.2s ease-out;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-3xl) var(--spacing-xl);
  position: relative;
  z-index: 1;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-3xl);
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  animation: ${fadeIn} 0.6s ease-out;
`;

const QuickNavTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const QuickNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
`;

const QuickNavItem = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  color: var(--text-secondary);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
    color: var(--davinci-blue);
    transform: translateX(4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: var(--davinci-blue);
    flex-shrink: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: var(--spacing-2xl);
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-2xl);
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%);
  border-radius: 12px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const SectionContent = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  color: var(--davinci-midnight);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const SectionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 600;
`;

const SectionText = styled.p`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-lg);
`;

const SubSection = styled.div`
  margin-bottom: var(--spacing-xl);
  padding-left: var(--spacing-lg);
  border-left: 2px solid rgba(59, 130, 246, 0.2);
`;

const SubSectionTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-xl);
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:after {
    content: "✓";
    position: absolute;
    left: 5px;
    top: 8px;
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const UnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-lg) 0;
`;

const DataTypeCard = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const DataTypeTitle = styled.h4`
  color: var(--davinci-midnight);
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 20px;
    height: 20px;
    color: var(--davinci-blue);
  }
`;

const DataTypeDescription = styled.p`
  color: var(--text-secondary);
  margin: 0;
  padding-left: var(--spacing-xl);
`;

const ImportantNote = styled.div`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin: var(--spacing-2xl) 0;
  display: flex;
  gap: var(--spacing-lg);
  align-items: flex-start;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-success);
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ImportantText = styled.p`
  color: var(--davinci-midnight);
  font-weight: 500;
  line-height: 1.8;
  margin: 0;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, var(--davinci-midnight) 0%, var(--davinci-blue) 100%);
  border-radius: 16px;
  padding: var(--spacing-2xl);
  margin-top: var(--spacing-3xl);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xl);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  svg {
    width: 28px;
    height: 28px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-xl);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  svg {
    width: 20px;
    height: 20px;
    opacity: 0.8;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

const PrivacyPolicy: React.FC = () => {
  const sections = [
    { id: 'purpose', title: 'Purpose', icon: <InformationCircleIcon /> },
    { id: 'information-collect', title: 'Information We Collect', icon: <FolderIcon /> },
    { id: 'how-collect', title: 'How We Collect', icon: <CloudIcon /> },
    { id: 'how-use', title: 'How We Use', icon: <CheckCircleIcon /> },
    { id: 'cookies', title: 'Cookie Policy', icon: <ComputerDesktopIcon /> },
    { id: 'storage', title: 'Data Storage', icon: <ServerIcon /> },
    { id: 'retention', title: 'Data Retention', icon: <ClockIcon /> },
    { id: 'sharing', title: 'Information Sharing', icon: <ShareIcon /> },
    { id: 'rights', title: 'User Rights', icon: <UserIcon /> },
    { id: 'children', title: "Children's Privacy", icon: <UserGroupIcon /> },
    { id: 'updates', title: 'Policy Updates', icon: <BellIcon /> },
    { id: 'contact', title: 'Contact Us', icon: <ChatBubbleLeftRightIcon /> },
  ];

  return (
    <PrivacyWrapper>
      <HeroSection>
        <HeroContent>
          <IconWrapper>
            <LockClosedIcon />
          </IconWrapper>
          <HeroTitle>Privacy Policy</HeroTitle>
          <HeroSubtitle>
            Your privacy matters. Learn how we protect your personal information and respect your data rights.
          </HeroSubtitle>
          <LastUpdatedBadge>
            <InformationCircleIcon />
            Last Updated: August 29, 2025
          </LastUpdatedBadge>
        </HeroContent>
      </HeroSection>

      <Container>
        <ScrollAnimation>
          <QuickNav>
            <QuickNavTitle>
              <DocumentTextIcon />
              Quick Navigation
            </QuickNavTitle>
            <QuickNavGrid>
              {sections.map((section) => (
                <QuickNavItem key={section.id} href={`#${section.id}`}>
                  {section.icon}
                  {section.title}
                </QuickNavItem>
              ))}
            </QuickNavGrid>
          </QuickNav>
        </ScrollAnimation>

        <ScrollAnimation>
          <ImportantNote>
            <ShieldCheckIcon />
            <ImportantText>
              At HL Networks Inc. OA Davinci AI Solutions, we are committed to protecting your privacy and the 
              confidentiality of your personal information. This Privacy Policy outlines how we collect, use, store, and 
              protect your data when you use our services.
            </ImportantText>
          </ImportantNote>
        </ScrollAnimation>

        <ContentGrid>
          <ScrollAnimation>
            <SectionCard id="purpose">
              <SectionHeader>
                <SectionIcon>
                  <InformationCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>1</SectionNumber>
                    Purpose of the Privacy Policy
                  </SectionTitle>
                  <SectionText>
                    This policy outlines how Davinci AI Solutions ("we," "us," or "our") collects, uses, stores, 
                    and protects personal information. Our commitment to data privacy is in line with Canadian privacy laws, 
                    specifically the Personal Information Protection and Electronic Documents Act (PIPEDA).
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="information-collect">
              <SectionHeader>
                <SectionIcon>
                  <FolderIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>2</SectionNumber>
                    Information We Collect
                  </SectionTitle>
                  <SectionText>
                    We may collect and process the following types of information from clients and users:
                  </SectionText>
                  
                  <DataTypeCard>
                    <DataTypeTitle>
                      <UserIcon />
                      Identifiers
                    </DataTypeTitle>
                    <DataTypeDescription>
                      Names, email addresses, phone numbers.
                    </DataTypeDescription>
                  </DataTypeCard>

                  <DataTypeCard>
                    <DataTypeTitle>
                      <ChartBarIcon />
                      Usage Data
                    </DataTypeTitle>
                    <DataTypeDescription>
                      Activity logs, interactions with IT systems under our management.
                    </DataTypeDescription>
                  </DataTypeCard>

                  <DataTypeCard>
                    <DataTypeTitle>
                      <ServerIcon />
                      Account Data
                    </DataTypeTitle>
                    <DataTypeDescription>
                      Administrator credentials (stored securely), client system access data, and other information 
                      necessary to fulfill IT support obligations.
                    </DataTypeDescription>
                  </DataTypeCard>

                  <DataTypeCard>
                    <DataTypeTitle>
                      <SparklesIcon />
                      User Sentiment and Personality Profiles
                    </DataTypeTitle>
                    <DataTypeDescription>
                      This information is used to tailor our support approach, allowing us to respond in ways that 
                      align with individual user preferences.
                    </DataTypeDescription>
                  </DataTypeCard>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="how-collect">
              <SectionHeader>
                <SectionIcon>
                  <CloudIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>3</SectionNumber>
                    How We Collect Information
                  </SectionTitle>
                  <SectionText>We gather information through:</SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>Direct Interactions:</strong> Information provided by clients for account setup, 
                      troubleshooting, or technical support.
                    </ListItem>
                    <ListItem>
                      <strong>Automated Monitoring:</strong> Backup processes, system performance tracking, and 
                      maintenance tasks.
                    </ListItem>
                    <ListItem>
                      <strong>AI-Driven Profiling:</strong> Analysis of user interactions through our ITSM system 
                      to generate personality insights for improved user experiences.
                    </ListItem>
                    <ListItem>
                      <strong>Third-Party Integrations:</strong> Collaborating with trusted third-party providers 
                      for data storage, backups, and secure access management.
                    </ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="how-use">
              <SectionHeader>
                <SectionIcon>
                  <CheckCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>4</SectionNumber>
                    How We Use Your Information
                  </SectionTitle>
                  <SectionText>Collected data supports:</SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>IT Support and System Maintenance:</strong> Including troubleshooting, updates, and 
                      security monitoring.
                    </ListItem>
                    <ListItem>
                      <strong>Account Management:</strong> To manage client relationships and respond to service requests.
                    </ListItem>
                    <ListItem>
                      <strong>Personalized User Interaction:</strong> Leveraging sentiment data and personality profiles 
                      to enhance service interactions based on individual preferences.
                    </ListItem>
                    <ListItem>
                      <strong>Backup and Recovery:</strong> For system resilience, ensuring data integrity and quick 
                      recovery in case of incidents.
                    </ListItem>
                    <ListItem>
                      <strong>Operational Reporting:</strong> Analyzing service trends to improve user experience.
                    </ListItem>
                  </UnorderedList>
                  
                  <ImportantNote style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                    <CheckCircleIcon />
                    <ImportantText>
                      <strong>Note:</strong> We do not sell, trade, or rent personal information to third parties.
                    </ImportantText>
                  </ImportantNote>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="cookies">
              <SectionHeader>
                <SectionIcon>
                  <ComputerDesktopIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>5</SectionNumber>
                    Cookie Policy
                  </SectionTitle>
                  <SectionText>
                    We use cookies and similar tracking technologies on our website and within our customer portal 
                    (Compass ITSM at https://compass.davincisolutions.ai) to enhance your experience and provide 
                    our services effectively.
                  </SectionText>
                  <SubSection>
                    <SubSectionTitle>Types of Cookies We Use:</SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Essential Cookies:</strong> Required for the operation of our website and Compass ITSM portal. 
                        These cookies enable core functionality such as security, network management, and accessibility.
                      </ListItem>
                      <ListItem>
                        <strong>Session Cookies:</strong> Temporary cookies that maintain your logged-in state while using 
                        Compass for ticket submission and tracking.
                      </ListItem>
                      <ListItem>
                        <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website to 
                        improve our services and user experience.
                      </ListItem>
                    </UnorderedList>
                  </SubSection>
                  <SectionText>
                    You can control cookies through your browser settings. However, disabling essential cookies may 
                    prevent you from accessing certain features of our services, particularly the Compass ticketing system.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="storage">
              <SectionHeader>
                <SectionIcon>
                  <ServerIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>6</SectionNumber>
                    Data Storage and Protection
                  </SectionTitle>
                  <SectionText>
                    We implement multiple layers of security, including:
                  </SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>Location:</strong> Data is stored on secure facilities located in Canada and the United States.
                    </ListItem>
                    <ListItem>
                      <strong>Encryption:</strong> All data is encrypted at rest and in transit to ensure privacy and security.
                    </ListItem>
                    <ListItem>
                      <strong>Access Controls:</strong> Only authorized personnel can access sensitive data, adhering to 
                      strict confidentiality requirements.
                    </ListItem>
                    <ListItem>
                      <strong>Cloud-Based Storage:</strong> User data is securely backed up on private cloud-based systems 
                      within Canada and the U.S. for redundancy and recovery.
                    </ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="retention">
              <SectionHeader>
                <SectionIcon>
                  <ClockIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>7</SectionNumber>
                    Data Retention
                  </SectionTitle>
                  <SectionText>
                    We retain personal information only as long as necessary to fulfill our services or as required by law. 
                    Data no longer needed is securely deleted in line with industry best practices.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="sharing">
              <SectionHeader>
                <SectionIcon>
                  <ShareIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>8</SectionNumber>
                    Information Sharing and Disclosure
                  </SectionTitle>
                  <SectionText>Information is shared solely with:</SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>Trusted Third-Party Providers:</strong> For essential services, such as data backup and 
                      secure storage, all located within Canada and bound by stringent privacy standards.
                    </ListItem>
                    <ListItem>
                      <strong>Legal Obligations:</strong> In compliance with applicable laws and regulations, if required.
                    </ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="rights">
              <SectionHeader>
                <SectionIcon>
                  <UserIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>9</SectionNumber>
                    User Rights and Choices
                  </SectionTitle>
                  <SectionText>Users have the right to:</SectionText>
                  <UnorderedList>
                    <ListItem>
                      <strong>Access Personal Information:</strong> Request the data we hold about them.
                    </ListItem>
                    <ListItem>
                      <strong>Correct Inaccuracies:</strong> Request corrections to their data.
                    </ListItem>
                    <ListItem>
                      <strong>Request Deletion:</strong> Request the deletion of personal information, subject to legal 
                      and business requirements.
                    </ListItem>
                    <ListItem>
                      <strong>Data Portability:</strong> We're happy to provide you with your data in a commonly used 
                      electronic format upon request, allowing you to transfer it to other service providers if needed.
                    </ListItem>
                  </UnorderedList>
                  
                  <SubSection>
                    <SubSectionTitle>Contact Preferences</SubSectionTitle>
                    <SectionText>
                      We respect your communication preferences. You can manage how we contact you:
                    </SectionText>
                    <UnorderedList>
                      <ListItem>
                        <strong>General Inquiries:</strong> Email us at info@davincisolutions.ai
                      </ListItem>
                      <ListItem>
                        <strong>Technical Support:</strong> For technical issues and support tickets, contact support@davincisolutions.ai
                      </ListItem>
                      <ListItem>
                        <strong>Opt-Out:</strong> You may opt out of marketing communications at any time by clicking the 
                        unsubscribe link in our emails or contacting us directly.
                      </ListItem>
                    </UnorderedList>
                  </SubSection>
                  
                  <SectionText>
                    For any privacy-related requests or questions about your rights, please contact us at: info@davincisolutions.ai
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="children">
              <SectionHeader>
                <SectionIcon>
                  <UserGroupIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>10</SectionNumber>
                    Children's Privacy
                  </SectionTitle>
                  <SectionText>
                    Our services are intended for users 18 and older. We do not knowingly collect data from children 
                    under this age.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="updates">
              <SectionHeader>
                <SectionIcon>
                  <BellIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>11</SectionNumber>
                    Policy Updates
                  </SectionTitle>
                  <SectionText>
                    We may update this policy periodically. Clients will be notified of significant changes, with 
                    updates posted to our website.
                  </SectionText>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>
        </ContentGrid>

        <ScrollAnimation>
          <ContactCard id="contact">
            <ContactContent>
              <ContactTitle>
                <ChatBubbleLeftRightIcon />
                Questions About Your Privacy?
              </ContactTitle>
              <SectionText style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: 'var(--spacing-xl)' }}>
                For questions or concerns about this Privacy Policy, contact us at:
              </SectionText>
              <ContactGrid>
                <ContactItem>
                  <EnvelopeIcon />
                  <a href="mailto:info@davincisolutions.ai">info@davincisolutions.ai</a>
                </ContactItem>
                <ContactItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+14038799343">403-879-9343</a>
                </ContactItem>
                <ContactItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>11-6320 11 Street SE, Calgary, AB T2H 2L7</span>
                </ContactItem>
              </ContactGrid>
            </ContactContent>
          </ContactCard>
        </ScrollAnimation>
      </Container>
    </PrivacyWrapper>
  );
};

// Add missing icons that were imported
const ChartBarIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SparklesIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

export default PrivacyPolicy;